/**
 * PURPOSE: Panel "Administración General" por compañía efectiva (FTR-0012).
 * RESPONSIBILITIES: Resolver la compañía de la pestaña desde ?idcompany= (validada contra las
 *   compañías administrables), mostrar selector y banner, y montar las pestañas solo con una
 *   compañía válida (key por compañía). Selecciones inválidas no emiten requests privilegiados.
 * COLLABORATORS: src/hooks/useEffectiveCompany.js, CompanyEventsTab, CompanyInstitutionsTab,
 *   CompanyVenuesTab, CompanyUniformsTab (FTR-0014, solo con UNIFORM_OPTIONS_ENABLED), CompanyRugbyLineupsTab
 *   (FTR-0015, solo con RUGBY_LINEUP_SUBMISSIONS_ENABLED).
 * ROUTE: winscore/src/sections/AdminCompanySection.jsx
 */
import React, { useState } from "react";
import { useSelector } from "react-redux";

import Title from "../ui/Title.jsx";
import CompanyEventsTab from "../admin/AdminCompany/CompanyEventsTab.jsx";
import CompanyInstitutionsTab from "../admin/AdminCompany/CompanyInstitutionsTab.jsx";
import CompanyVenuesTab from "../admin/AdminCompany/CompanyVenuesTab.jsx";
import CompanyUniformsTab from "../admin/AdminCompany/CompanyUniformsTab.jsx";
import CompanyRugbyLineupsTab from "../admin/AdminCompany/CompanyRugbyLineupsTab.jsx";
import { useEffectiveCompany } from "../hooks/useEffectiveCompany.js";
import { useUniformOptionsStatus } from "../hooks/useUniformOptionsStatus.js";
import { useRugbyLineupsStatus } from "../hooks/useRugbyLineupsStatus.js";

const TABS = [
  { id: "events", label: "Eventos" },
  { id: "institutions", label: "Instituciones" },
  { id: "venues", label: "Sedes" },
];
// Opciones de uniforme normalizadas (FTR-0014): la pestaña existe solo con el flag del backend encendido.
const UNIFORMS_TAB = { id: "uniforms", label: "Uniformes" };
// Elegibles Rugby (FTR-0015): la pestaña existe solo con los envíos encendidos en el backend.
const RUGBY_LINEUPS_TAB = { id: "rugby-lineups", label: "Elegibles Rugby" };

const STATUS_MESSAGES = {
  forbidden: "Tu usuario no puede administrar compañías.",
  error: "No se pudieron cargar las compañías que administras. Intenta nuevamente.",
  empty: "No tienes compañías activas disponibles para administrar.",
  invalid: "La compañía indicada no está disponible para tu usuario. Selecciona una de la lista.",
  select: "Selecciona la compañía que deseas administrar.",
};

const companyLabel = (company) =>
  company ? `${company.name}${company.abbreviation ? ` (${company.abbreviation})` : ""}` : "";

const Notice = ({ tone = "info", children }) => {
  const tones = {
    info: "border-blue-200 bg-blue-50 text-blue-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-xl border px-5 py-4 text-sm ${tones[tone] || tones.info}`}>
      {children}
    </div>
  );
};

const CompanySelector = ({ companies, value, onChange }) => (
  <label className="block max-w-xl text-sm font-medium text-slate-700 dark:text-slate-200">
    Compañía
    <select
      value={value ? String(value) : ""}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
    >
      <option value="">Seleccione una compañía</option>
      {companies.map((company) => (
        <option key={company.id} value={company.id}>
          {companyLabel(company)}
        </option>
      ))}
    </select>
  </label>
);

const AdminCompanySection = () => {
  const [activeTab, setActiveTab] = useState("events");
  const yearSelected = useSelector((state) => state.year.yearSelected);
  const { status, effectiveCompanyId, company, companies, canSwitch, setCompany } =
    useEffectiveCompany();
  const uniformOptions = useUniformOptionsStatus();
  const rugbyLineups = useRugbyLineupsStatus();
  const tabs = [
    ...TABS,
    ...(uniformOptions.enabled ? [UNIFORMS_TAB] : []),
    ...(rugbyLineups.submissionsEnabled ? [RUGBY_LINEUPS_TAB] : []),
  ];

  const showSelector = companies.length > 0 && (canSwitch || status !== "ready");

  return (
    <div className="w-full px-4 py-4">
      <div className="mx-auto max-w-7xl">
        <div className="mb-5">
          <Title>Administración General</Title>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
            Gestiona Eventos, Instituciones y Sedes de la compañía seleccionada.
          </p>
        </div>

        {status === "loading" || status === "redirect" ? (
          <div className="rounded-xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500">
            Cargando compañías...
          </div>
        ) : (
          <div className="space-y-5">
            {status === "ready" && company && (
              <div
                className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 sm:flex-row sm:items-end sm:justify-between"
                role="status"
              >
                <p className="text-sm text-blue-900">
                  Administrando: <strong>{companyLabel(company)}</strong>
                </p>
                {canSwitch && (
                  <CompanySelector
                    companies={companies}
                    value={effectiveCompanyId}
                    onChange={setCompany}
                  />
                )}
              </div>
            )}

            {status !== "ready" && (
              <>
                {STATUS_MESSAGES[status] && (
                  <Notice tone={status === "select" ? "info" : "warning"}>
                    {STATUS_MESSAGES[status]}
                  </Notice>
                )}
                {showSelector && (
                  <CompanySelector companies={companies} value={null} onChange={setCompany} />
                )}
              </>
            )}

            {status === "ready" && effectiveCompanyId && (
              <div key={effectiveCompanyId}>
                <div
                  className="mb-5 flex overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-600 dark:bg-gray-700"
                  role="tablist"
                  aria-label="Administración de compañía"
                >
                  {tabs.map((tab) => {
                    const selected = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => setActiveTab(tab.id)}
                        className={`min-w-[140px] flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                          selected
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-600"
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                <div role="tabpanel">
                  {activeTab === "events" && (
                    <CompanyEventsTab idcompany={effectiveCompanyId} periodo={yearSelected} />
                  )}
                  {activeTab === "institutions" && (
                    <CompanyInstitutionsTab idcompany={effectiveCompanyId} />
                  )}
                  {activeTab === "venues" && <CompanyVenuesTab idcompany={effectiveCompanyId} />}
                  {activeTab === "uniforms" && uniformOptions.enabled && (
                    <CompanyUniformsTab idcompany={effectiveCompanyId} />
                  )}
                  {activeTab === "rugby-lineups" && rugbyLineups.submissionsEnabled && (
                    <CompanyRugbyLineupsTab idcompany={effectiveCompanyId} />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCompanySection;
